import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import CloseIcon from 'assets/icons/close.svg';
import useKeypress from 'hooks/useKeypress';
import useModifiers from 'hooks/useModifiers';
import Btn from './btn';
import Skeleton from './skeleton';

export enum ModalModifier {
  WIDE = 'wide',
}

export interface ModalProps {
  showModal: boolean;
  isLoading: boolean;
  children: React.ReactElement;
  btnText?: string;
  title?: string | React.ReactElement;
  headline?: string | React.ReactElement;
  description?: string | React.ReactElement;
  onModalClose: () => void;
  onModalAction: () => void;
  modifiers?: ModalModifier[];
}

function Modal(props: ModalProps) {
  const modalRoot = document.getElementById('ct_modal') as HTMLElement;
  const body = document.querySelector('body') as HTMLElement;
  const escPressed = useKeypress('Escape');
  const mods = useModifiers(props.modifiers);

  const {
    showModal,
    isLoading,
    children,
    btnText = '',
    title = '',
    headline,
    description = '',
    onModalAction,
    onModalClose,
  } = props;

  useEffect(() => {
    if (body && showModal) {
      body.classList.add('ct_preventScroll');
    } else if (body) {
      body.classList.remove('ct_preventScroll');
    }
  }, [showModal]);

  useEffect(() => {
    if (showModal && escPressed) {
      onModalClose();
    }
  }, [escPressed]);

  return modalRoot
    ? ReactDOM.createPortal(
      showModal ? (
        <div className="ct_modal-container" aria-hidden="true" onClick={() => onModalClose()}>
          <div className={`ct_modal-dialog ${mods}`} aria-hidden="true" onClick={(e) => e.stopPropagation()}>
            <div className="ct_modal-content">
              <div className="ct_modal-header">
                <div className="ct_modal-headerContent">
                  {!!title && (<div className="ct_modal-title">{title}</div>)}
                  {!!headline && (<div className="ct_modal-headline">{headline}</div>)}
                  {!!description && (<div className="ct_modal-description">{description}</div>)}
                </div>
                <div className="ct_modal-close">
                  <Btn type="link" icon={<CloseIcon />} onClick={() => onModalClose()} />
                </div>
              </div>
              <div className="ct_modal-body">{children}</div>
              {!!btnText && (
                <div className="ct_modal-footer">
                  {isLoading && (
                    <Skeleton width="160px" height="50px" />
                  )}
                  {!isLoading && (
                    <Btn type="primary" text={btnText} onClick={() => onModalAction()} />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null,
      modalRoot,
    )
    : null;
}

export default Modal;
